import _ from 'lodash';
import { observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { ImageFile } from 'react-dropzone';
import type { ParticipantCompanyJson } from './participant_company';
import ParticipantCompany from './participant_company';
import type { ReportJson } from './report';
import { Report } from './report';
import type { ExpensesTypeJson } from './expenses_type';
import ExpensesType from './expenses_type';
import type { ProjectArgs } from '../project/project';
import Project from '../project/project';
import type { TaxTypeArgs } from '../tax_type';
import TaxType from '../tax_type';

export type Category =
  | 'general_expenses'
  | 'travel_expenses'
  | 'travel_expenses_daily_allowance'
  | 'reception_expenses';

export type TripType = 'round_trip' | 'one_way' | 'other';

export const MAX_VIA_POINTS = 3;

export interface ViaPointJson {
  station_name: string;
  position: number;
}

// 経由地は行の追加・削除で並びが変わるため、Reactのkey用に行ごとの識別子を持たせる
export interface ViaPoint {
  key: string;
  stationName: string;
}

export interface ReceiptFileJson {
  id: number;
  file_path: string;
  content_type: string;
  original_filename: string;
  position: number;
}

export interface ItemJson {
  id?: number;
  payee: string;
  paid_at: string;
  price: number;
  expense_type: string;
  expenses_type: ExpensesTypeJson | null;
  use_ic: boolean;
  project?: ProjectArgs;
  memo: string;
  category: Category;
  from: string;
  to: string;
  trip_type: TripType;
  participant_companies: ParticipantCompanyJson[];
  railway_name?: string;
  price_average?: number;
  expenses_report_id?: number;
  created_at: string;
  updated_at: string;
  category_options?: { [key: string]: string };
  expense_type_options?: { [key: string]: string };
  receipt_file_path?: string;
  receipt_files_data?: ReceiptFileJson[];
  via_points_data?: ViaPointJson[];
  expenses_report: ReportJson;
  tax_type_id?: number;
  non_order_item?: {
    id: number;
    trip_report_id: number;
    trip_report: {
      id: number;
      name: string;
    };
  };
  tax_type?: TaxTypeArgs;
  invoice_number?: string | null;
}

export interface ReceiptFileEntry {
  id?: number;
  filePath: string;
  contentType: string;
  originalFilename: string;
  position: number;
  file?: ImageFile;
}

export class Item {
  readonly id?: number;

  payee: string;

  paidAt: Moment;

  price: number;

  priceDisplay: string;

  expenseType: string;

  expensesType: ExpensesType | null;

  useIc: boolean;

  project: Project | null;

  memo: string;

  category: Category;

  from: string;

  to: string;

  tripType: TripType;

  @observable viaPoints: ViaPoint[] = [];

  participantCompanies: ParticipantCompany[];

  railwayName: string;

  priceAverageDisplay: string;

  taxTypeId: number | null;

  taxType?: TaxType;

  invoiceNumber: string;

  @observable receiptFilePath?: string;
  @observable receiptFile?: ImageFile;
  @observable receiptFiles: ReceiptFileEntry[] = [];
  @observable newReceiptFiles: ImageFile[] = [];
  @observable deleteReceiptFileIds: number[] = [];

  readonly reportId?: number;

  readonly createdAt: Moment;

  readonly updatedAt: Moment;

  readonly categoryOptions?: { [key: string]: string };

  readonly expenseTypeOptions?: { [key: string]: string };

  selected: boolean;

  report?: Report;

  tripReport?: { id: number; name: string };

  constructor(args: ItemJson) {
    this.id = args.id;
    this.payee = args.payee || '';
    this.paidAt = args.paid_at ? moment(args.paid_at) : moment();
    this.price = args.price;
    this.priceDisplay = new Intl.NumberFormat().format(args.price);
    this.expenseType = args.expense_type || '';
    this.expensesType = args.expenses_type ? new ExpensesType(args.expenses_type) : null;
    // 過去データだとuse_ic=nullの可能性がある
    this.useIc = args.use_ic != null ? args.use_ic : true;
    this.project = args.project ? new Project(args.project) : null;
    this.memo = args.memo || '';
    this.category = args.category;
    this.from = args.from || '';
    this.to = args.to || '';
    this.tripType = args.trip_type;
    this.viaPoints = (args.via_points_data || []).map(vp => Item.buildViaPoint(vp.station_name));
    this.participantCompanies = args.participant_companies
      ? args.participant_companies.map(c => new ParticipantCompany(c))
      : [];
    this.railwayName = args.railway_name || '';
    this.priceAverageDisplay = '';
    this.setPriceAverageDisplay();
    this.reportId = args.expenses_report_id;
    this.createdAt = moment(args.created_at);
    this.updatedAt = moment(args.updated_at);
    this.categoryOptions = args.category_options;
    this.expenseTypeOptions = args.expense_type_options;
    this.selected = false;
    this.receiptFilePath = args.receipt_file_path;
    this.receiptFiles = (args.receipt_files_data || []).map(rf => ({
      id: rf.id,
      filePath: rf.file_path,
      contentType: rf.content_type,
      originalFilename: rf.original_filename,
      position: rf.position
    }));
    this.newReceiptFiles = [];
    this.deleteReceiptFileIds = [];
    this.report = args.expenses_report && new Report(args.expenses_report);
    this.tripReport = args.non_order_item && {
      id: args.non_order_item.trip_report_id,
      name: args.non_order_item.trip_report.name
    };
    this.taxTypeId = args.tax_type_id || null;
    this.taxType = args.tax_type && new TaxType(args.tax_type);
    this.invoiceNumber = args.invoice_number || '';
  }

  participantCompaniesToDisplay(): ParticipantCompany[] {
    return this.participantCompanies.filter(c => !c.destroy);
  }

  findOwnCompany(): ParticipantCompany | undefined {
    return this.participantCompanies.find(c => c.own && c.destroy !== '1');
  }

  ownCompany(): ParticipantCompany {
    const own = this.findOwnCompany();
    if (own) return own;
    const c = new ParticipantCompany({ name: '', people_num: 1, own: true });
    this.participantCompanies.push(c);
    return c;
  }

  otherCompaniesToDisplay(): ParticipantCompany[] {
    return this.participantCompaniesToDisplay().filter(c => !c.own);
  }

  setPayee(value: string) {
    this.payee = value;
    app.render();
  }

  setPaidAt(value: string) {
    this.paidAt = moment(value);
    app.render();
  }

  setPrice(value: number) {
    this.price = value;
    app.render();
  }

  setExpensesType(value: ExpensesType | null) {
    this.expensesType = value;
    // 科目の選択解除（null）では破棄しない。解除→同じ科目の再選択で入力が失われないようにするため
    if (value !== null && !this.needParticipantCompanies()) {
      this.discardParticipantCompanies();
    }
    // 経由地の入力欄は categoryIsTravelExpenses() の間だけ表示される。経費科目の変更で
    // その表示条件から外れた場合、入力欄は消えるのに trip_type/経由地は残ってしまうため
    // ここで一緒にクリアする(setTripTypeでtripTypeが変わる場合と同じ扱い)。
    if (!this.categoryIsTravelExpenses()) {
      this.tripType = 'one_way';
      this.viaPoints = [];
    }
    this.setPriceAverageDisplay();
  }

  // 一括更新中は render せず、呼び出し元の公開メソッドが最後に1回だけ render する
  private discardParticipantCompanies() {
    this.participantCompanies = this.participantCompanies.filter(c => c.id);
    this.participantCompanies.forEach(c => {
      c.destroy = '1';
    });
  }

  toggleUseIc() {
    this.useIc = !this.useIc;
    app.render();
  }

  setProject(value: Project | null) {
    this.project = value;
    app.render();
  }

  setMemo(value: string) {
    this.memo = value;
    app.render();
  }

  setCategory(value: Category) {
    this.category = value;
    this.expenseType = '';
    app.render();
  }

  setFrom(value: string) {
    this.from = value;
    app.render();
  }

  setTo(value: string) {
    this.to = value;
    app.render();
  }

  setTripType(value: TripType) {
    this.tripType = value;
    // 片道・往復は出発地と到着地だけで経路が決まるため、経由地は複数拠点間の移動(other)のときだけ保持する
    if (value !== 'other') {
      this.viaPoints = [];
    }
    app.render();
  }

  static buildViaPoint(stationName: string): ViaPoint {
    return { key: _.uniqueId('via_point_'), stationName };
  }

  setViaPoint(key: string, value: string) {
    this.viaPoints = this.viaPoints.map(vp => (vp.key === key ? { ...vp, stationName: value } : vp));
    app.render();
  }

  addViaPoint() {
    if (this.viaPoints.length >= MAX_VIA_POINTS) return;

    this.viaPoints = [...this.viaPoints, Item.buildViaPoint('')];
    app.render();
  }

  removeViaPoint(key: string) {
    this.viaPoints = this.viaPoints.filter(vp => vp.key !== key);
    app.render();
  }

  filledViaPoints(): string[] {
    return this.viaPoints.map(vp => vp.stationName.trim()).filter(stationName => stationName.length > 0);
  }

  addParticipantCompany() {
    const c = new ParticipantCompany({ name: '', people_num: 1, own: false });
    this.participantCompanies.push(c);
    app.render();
  }

  setRailwayName(value: string) {
    this.railwayName = value;
    app.render();
  }

  totalPeopleNum(): number {
    return this.participantCompaniesToDisplay()
      .filter(c => !Number.isNaN(c.peopleNum))
      .reduce((sum, c) => sum + c.peopleNum, 0);
  }

  setPriceAverageDisplay() {
    const totalPeopleNum = this.totalPeopleNum();
    if (this.price === null || totalPeopleNum === 0) {
      this.priceAverageDisplay = '--円';
    } else {
      const average = Math.round(this.price / totalPeopleNum);
      this.priceAverageDisplay = `${average.toLocaleString()}円`;
    }
    app.render();
  }

  setTaxTypeId(value: number | null) {
    this.taxTypeId = value;
    app.render();
  }

  setInvoiceNumber(value: string) {
    this.invoiceNumber = value;
    app.render();
  }

  toggleSelected() {
    this.selected = !this.selected;
    app.render();
  }

  categoryIsTravelExpenses(): boolean {
    return (
      this.category === 'travel_expenses' &&
      !this.expensesTypeIsTrain() &&
      !this.expensesTypeIsBus() &&
      !this.expensesTypeIsTaxi()
    );
  }

  expensesTypeIsTrain(): boolean {
    return this.expensesType?.isTrain() ?? false;
  }

  expensesTypeIsTaxi(): boolean {
    return this.expensesType?.isTaxi() ?? false;
  }

  expensesTypeIsBus(): boolean {
    return this.expensesType?.isBus() ?? false;
  }

  showDistance(): boolean {
    if (!this.expensesType) return false;
    const category = this.expensesType.category;
    return this.expensesType.isDailyAllowance() || category === 'travel_expenses_daily_allowance';
  }

  needParticipantCompanies(): boolean {
    return this.expensesType?.isNeedParticipants() ?? false;
  }

  // 状態を変更しない純粋なシリアライズ。参加企業不要な科目では保存済み行の削除指示のみを送る
  participantCompaniesToSubmit() {
    if (this.needParticipantCompanies()) {
      return this.participantCompanies.map(c => c.submitParams());
    }
    return this.participantCompanies.filter(c => c.id).map(c => ({ ...c.submitParams(), destroy: '1' }));
  }

  validateParticipantCompanies(): string[] {
    if (!this.needParticipantCompanies()) return [];
    const errors: string[] = [];
    const own = this.findOwnCompany();
    if (own) {
      const error = Item.peopleNumError(own);
      if (error) errors.push(error);
    }
    this.otherCompaniesToDisplay().forEach((c, i) => {
      if (c.name.trim() === '') errors.push(`${i + 1}行目: 参加企業名を入力してください`);
      const error = Item.peopleNumError(c);
      if (error) errors.push(`${i + 1}行目: ${error}`);
    });
    return errors;
  }

  private static peopleNumError(company: ParticipantCompany): string | null {
    if (Number.isNaN(company.peopleNum)) return '参加人数を入力してください';
    if (!company.hasValidPeopleNum()) return '参加人数は1以上の整数で入力してください';
    return null;
  }

  addReceiptFiles(files: ImageFile[]) {
    this.newReceiptFiles = [...this.newReceiptFiles, ...files];
    app.render();
  }

  removeNewReceiptFile(index: number) {
    this.newReceiptFiles = this.newReceiptFiles.filter((_, i) => i !== index);
    app.render();
  }

  removeExistingReceiptFile(fileId: number) {
    this.deleteReceiptFileIds = [...this.deleteReceiptFileIds, fileId];
    this.receiptFiles = this.receiptFiles.filter(rf => rf.id !== fileId);
    app.render();
  }

  get allReceiptFilesCount(): number {
    return (
      this.receiptFiles.length + this.newReceiptFiles.length + (this.receiptFilePath || this.receiptFile ? 1 : 0)
    );
  }

  submitParams() {
    const params = {
      category: this.category,
      payee: this.payee,
      paid_at: this.paidAt.format('YYYY-MM-DD'),
      price: this.price,
      expenses_type_id: this.expensesType?.id,
      use_ic: this.useIc,
      project_id: this.project?.id,
      memo: this.memo,
      from: this.from,
      to: this.to,
      trip_type: this.tripType,
      via_points: this.filledViaPoints(),
      participant_companies_attributes: this.participantCompaniesToSubmit(),
      railway_name: this.railwayName,
      receipt_file_path: this.receiptFilePath ? this.receiptFilePath : '',
      tax_type_id: this.taxTypeId,
      invoice_number: this.invoiceNumber
    };
    return params;
  }

  ekispertPriceParams() {
    return {
      from: this.from,
      to: this.to,
      date: this.paidAt.format('YYYY/MM/DD'),
      trip_type: this.tripType,
      via_points: this.filledViaPoints()
    };
  }

  editable() {
    return !this.report || this.report?.status === 'draft' || this.report?.status === 'rejected';
  }

  get formData() {
    const data = new FormData();
    if (this.receiptFile) {
      data.append('file', this.receiptFile);
    }
    this.newReceiptFiles.forEach(f => {
      data.append('receipt_files[]', f);
    });
    this.deleteReceiptFileIds.forEach(id => {
      data.append('delete_receipt_file_ids[]', id.toString());
    });
    const params = this.submitParams();
    data.append('category', params.category);
    data.append('payee', params.payee);
    data.append('paid_at', params.paid_at);
    data.append('price', params.price ? params.price.toString() : '');
    data.append('expenses_type_id', params.expenses_type_id ? params.expenses_type_id.toString() : '');
    data.append('use_ic', params.use_ic ? 'true' : 'false');
    data.append('project_id', params.project_id ? params.project_id.toString() : '');
    data.append('tax_type_id', params.tax_type_id ? params.tax_type_id.toString() : '');
    data.append('memo', params.memo);
    data.append('from', params.from);
    data.append('to', params.to);
    data.append('trip_type', params.trip_type);
    params.via_points.forEach(viaPoint => {
      data.append('via_points[]', viaPoint);
    });
    data.append('receipt_file_path', params.receipt_file_path);
    _.each(params.participant_companies_attributes, (p, i) => {
      if (p.id) {
        data.append(`participant_companies_attributes[${i}][id]`, p.id.toString());
      }
      data.append(`participant_companies_attributes[${i}][name]`, p.name);
      data.append(
        `participant_companies_attributes[${i}][people_num]`,
        p.people_num == null ? '' : p.people_num.toString()
      );
      data.append(`participant_companies_attributes[${i}][own]`, p.own ? 'true' : 'false');
      data.append(`participant_companies_attributes[${i}][_destroy]`, p.destroy ? 'true' : 'false');
    });
    data.append('railway_name', params.railway_name);
    data.append('invoice_number', params.invoice_number);
    return data;
  }
}

export default Item;
